import { useEffect, useRef, useState } from "react";
import { callApi } from "../api/api";

const INITIAL_DUPLICATE_STATE = {
  codeTaken: false,
  nameTaken: false,
  checking: false,
};

export function useServerDuplicateCheck({
  enabled,
  instance,
  accounts,
  url,
  params,
  debounceMs = 400,
}) {
  const [duplicateState, setDuplicateState] = useState(INITIAL_DUPLICATE_STATE);
  const controllerRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !accounts.length || !url) {
      setDuplicateState(INITIAL_DUPLICATE_STATE);
      return undefined;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      setDuplicateState((prev) => ({
        ...prev,
        checking: true,
      }));

      try {
        const res = await callApi(instance, accounts, "GET", url, null, {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        const payload = res?.data?.data ?? res?.data ?? {};

        setDuplicateState({
          codeTaken: !!payload.codeTaken,
          nameTaken: !!payload.nameTaken,
          checking: false,
        });
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Duplicate check failed", err);
        setDuplicateState(INITIAL_DUPLICATE_STATE);
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [
    enabled,
    instance,
    accounts,
    url,
    debounceMs,
    params?.category,
    params?.code,
    params?.name,
    params?.excludeId,
  ]);

  return duplicateState;
}
